/**
 * NavState → 两种布局的投影规则（纯函数，单测锁定）。
 * 同一个栈，H5 渲染栈顶一页，PC 双栏各取一个维度——跨断点缩放只是换投影，
 * 会话级状态天然不丢（修复 legacy §4.5）。
 *
 * - H5：渲染 topRoute（栈顶）。
 * - PC 左栏：activeHomeTab = 从栈顶往下最近一个 home 条目的 tab（没有则 chats）。
 * - PC 右栏：栈顶若是**详情类路由**（好友详情 / 群设置及子页 / 我的各子页…）就渲染它，
 *   否则 activeChatKey = 从栈顶往下最近一个 chat 条目（没有则空态占位）。
 *   例：[home(chats), chat(dm:5), home(contacts)] → 左栏通讯录、右栏 dm:5；
 *   同一栈在 H5 显示通讯录页，返回键回到 dm:5。
 *
 * 详情进右栏是 21o 改的（人工：「PC端点击用户不要跳转到新的界面应该是在旁边窗口打开」）。
 * 在那之前桌面端对这些路由是**全屏接管**——点一下好友，左栏列表也没了，
 * 那是「把移动端的页面栈直接搬到桌面」。
 */
import { invariant } from '~/util/invariant';
import type { NavState } from './router';
import type { AppRoute, HomeTab } from './routes';

export function topRoute(nav: NavState): AppRoute {
  const top = nav.stack[nav.stack.length - 1];
  invariant(top, 'NavState 栈为空');
  return top;
}

export function activeHomeTab(nav: NavState): HomeTab {
  for (let i = nav.stack.length - 1; i >= 0; i--) {
    const entry = nav.stack[i];
    if (entry?.kind === 'home') return entry.tab;
  }
  return 'chats';
}

export function activeChatKey(nav: NavState): string | undefined {
  for (let i = nav.stack.length - 1; i >= 0; i--) {
    const entry = nav.stack[i];
    if (entry?.kind === 'chat') return entry.conversationKey;
  }
  return undefined;
}

/**
 * 桌面端**仍然全屏接管**的路由（21o）。其余一律进右栏。
 *
 * 留在这里的四类，各有各的理由，别顺手挪进右栏：
 * - `login` / `set-password` / `add-account`：登录态流程，此时根本没有左栏可言；
 * - `dev-vlist`：压测用的开发页；
 * - `install`：安装引导，文案是「换 iPhone Safari 打开」，双栏无意义（原注释）；
 * - `archive`：归档**会话列表**——它是个列表不是详情，塞进右栏观感错位；
 *   真要做应该是占左栏，归后续任务；
 * - `me-qrcode` / `group-qrcode`：二维码展示偏模态，且页面假设自己占满宽度。
 */
const DESKTOP_FULLSCREEN: ReadonlySet<AppRoute['kind']> = new Set([
  'login',
  'set-password',
  'add-account',
  'dev-vlist',
  'install',
  'archive',
  'me-qrcode',
  'group-qrcode',
]);

export function isDesktopFullscreen(kind: AppRoute['kind']): boolean {
  return DESKTOP_FULLSCREEN.has(kind);
}

/**
 * 详情路由指向的会话（没有就是 undefined）——判定「这个详情是不是当前会话的资料」用。
 * 只列**从聊天标题能到达**的那几支；我的各子页/通讯录列表类不属于任何会话。
 */
function routeConversationKey(route: AppRoute): string | undefined {
  switch (route.kind) {
    case 'group-settings':
    case 'group-manage':
    case 'group-members':
    case 'group-invite':
    case 'group-log':
    case 'group-notice':
      return `group:${route.groupId}`;
    case 'friend-detail':
      return `dm:${route.friendId}`;
    default:
      return undefined;
  }
}

/**
 * 桌面**第三栏（资料面板）**要显示的路由（21q，人工给图：群聊 PC 应是
 * 「聊天区 + 右侧资料面板」**并存**，不是把聊天替换掉）。
 *
 * 同一个目标路由（如 `group-settings`）有两条进入路径，投影必须区分：
 * - **从聊天标题进**（栈里这个详情**压在对应会话之上**）→ 资料面板，与聊天并存；
 * - **从通讯录进**（底下没有对应会话，只有 home）→ 照旧占右栏，没有聊天可并存。
 *
 * 判定不靠给路由加来源标记（那会进 URL、刷新后还得维护），而是**从栈形状推**：
 * 栈顶详情指向会话 K，且 K 的 chat 条目在栈里**高于最近的 home 条目**
 * → 这一串是从那个会话里点进来的。面板里再往下点（成员→群管理）也成立，
 * 因为那些子页指向同一个 K。
 */
export function activePanelRoute(nav: NavState): AppRoute | undefined {
  const top = topRoute(nav);
  if (top.kind === 'home' || top.kind === 'chat' || isDesktopFullscreen(top.kind)) return undefined;
  const key = routeConversationKey(top);
  if (key === undefined) return undefined;
  for (let i = nav.stack.length - 2; i >= 0; i--) {
    const entry = nav.stack[i];
    if (entry === undefined) continue;
    if (entry.kind === 'home') return undefined; // 先撞到 home → 是从列表进来的
    if (entry.kind === 'chat') return entry.conversationKey === key ? top : undefined;
  }
  return undefined;
}

/**
 * 桌面右栏要显示的详情路由：栈顶既不是 home 也不是 chat、且不在全屏白名单里时，
 * 就是它。返回 undefined = 右栏照旧显示 activeChatKey 的会话（或空态）。
 *
 * 21q 起**排除资料面板那一支**（activePanelRoute）：那种情况右栏仍是聊天，
 * 详情另开第三栏。
 */
export function activeDetailRoute(nav: NavState): AppRoute | undefined {
  const top = topRoute(nav);
  if (top.kind === 'home' || top.kind === 'chat') return undefined;
  if (isDesktopFullscreen(top.kind)) return undefined;
  return activePanelRoute(nav) !== undefined ? undefined : top;
}
